import { create } from "@bufbuild/protobuf";
import { type FieldMask } from "@bufbuild/protobuf/wkt";
import {
	ArchiveDashboardRequestSchema,
	CreateDashboardRequestSchema,
	CreateScheduledReportRequestSchema,
	DeleteDashboardRequestSchema,
	ExecuteReportRequestSchema,
	GetDashboardRequestSchema,
	GetDashboardsRequestSchema,
	GetScheduledReportRequestSchema,
	ListDashboardsRequestSchema,
	ListScheduledReportsRequestSchema,
	ReportsService,
	RestoreDashboardRequestSchema,
	TransferScheduledReportRequestSchema,
	TriggerScheduledReportRequestSchema,
	UpdateDashboardRequestSchema,
	UpdateScheduledReportRequestSchema,
	type ArchiveDashboardResponse,
	type CreateDashboardResponse,
	type CreateScheduledReportResponse,
	type Dashboard,
	type DashboardLocation,
	type DashboardState,
	type DeleteDashboardResponse,
	type DeleteScheduledReportResponse,
	type ExecuteReportResponse,
	type GetDashboardResponse,
	type GetDashboardsResponse,
	type GetScheduledReportResponse,
	type ListDashboardsResponse,
	type ListScheduledReportsResponse,
	type Report,
	type RestoreDashboardResponse,
	type ScheduledReport,
	type ScheduledReportFrequency,
	type ScheduledReportState,
	type ScheduledReportType,
	type ToggleScheduledReportResponse,
	type TransferScheduledReportResponse,
	type TriggerScheduledReportResponse,
	type UpdateDashboardResponse,
	type UpdateScheduledReportResponse,
} from "#gen/abacus/v2/reports_pb.js";
import type { PaginationArguments } from "#gen/common/v1/pagination_pb.js";
import type { Creator } from "#gen/common/v1/types_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export class ReportsClient {
	private client: Client<typeof ReportsService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.abacus || "/", ReportsService, options);
	}

	async listScheduledReports({
		orgId,
		workspaceId,
		suiteId,
		name,
		userId,
		creatorEmail,
		type,
		frequency,
		pagination,
		state,
		dashboardId,
	}: {
		orgId?: string;
		workspaceId?: string;
		suiteId?: string;
		name?: string;
		userId?: string;
		creatorEmail?: string;
		type?: ScheduledReportType;
		frequency?: ScheduledReportFrequency;
		pagination?: PaginationArguments;
		state?: ScheduledReportState;
		dashboardId?: string;
	}): Promise<ListScheduledReportsResponse> {
		try {
			const params = create(ListScheduledReportsRequestSchema, {
				orgId,
				workspaceId,
				suiteId,
				name,
				userId,
				creatorEmail,
				type,
				frequency,
				state,
				pagination,
				dashboardId,
			});
			const res = await this.client.listScheduledReports(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async pauseReport(id: string): Promise<ToggleScheduledReportResponse> {
		try {
			const res = await this.client.toggleScheduledReport({
				id,
				pause: true,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async unpauseReport(id: string): Promise<ToggleScheduledReportResponse> {
		try {
			const res = await this.client.toggleScheduledReport({
				id,
				pause: false,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteReport(id: string): Promise<DeleteScheduledReportResponse> {
		try {
			const res = await this.client.deleteScheduledReport({ id });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createScheduledReport(scheduledReport: ScheduledReport): Promise<CreateScheduledReportResponse> {
		try {
			const params = create(CreateScheduledReportRequestSchema, { scheduledReport });
			const res = await this.client.createScheduledReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateScheduledReport(scheduledReport: ScheduledReport): Promise<UpdateScheduledReportResponse> {
		try {
			const params = create(UpdateScheduledReportRequestSchema, { scheduledReport });
			const res = await this.client.updateScheduledReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async transferScheduledReport(id: string, newOwner: Creator): Promise<TransferScheduledReportResponse> {
		try {
			const params = create(TransferScheduledReportRequestSchema, { id, creator: newOwner });
			const res = await this.client.transferScheduledReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getScheduledReport(id: string): Promise<GetScheduledReportResponse> {
		try {
			const params = create(GetScheduledReportRequestSchema, { id });
			const res = await this.client.getScheduledReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listDashboards(orgId?: string, workspaceId?: string, suiteId?: string, name?: string, userIds?: string[], creatorEmails?: string[], workspaceIds?: string[], pagination?: PaginationArguments, states?: DashboardState[], options?: CallOptions, location?: DashboardLocation, locations?: DashboardLocation[]): Promise<ListDashboardsResponse> {
		try {
			const params = create(ListDashboardsRequestSchema, {
				orgId,
				workspaceId,
				suiteId,
				name,
				userIds,
				creatorEmails,
				workspaceIds,
				pagination,
				states,
				location,
				locations,
			});
			const res = await this.client.listDashboards(params, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createDashboard(dashboard: Dashboard): Promise<CreateDashboardResponse> {
		try {
			const params = create(CreateDashboardRequestSchema, { dashboard });
			const res = await this.client.createDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateDashboard(dashboard: Dashboard, mask?: FieldMask): Promise<UpdateDashboardResponse> {
		try {
			const params = create(UpdateDashboardRequestSchema, {
				dashboard,
				mask,
			});
			const res = await this.client.updateDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async triggerScheduledReport(id: string): Promise<TriggerScheduledReportResponse> {
		try {
			const params = create(TriggerScheduledReportRequestSchema, { id });
			const res = await this.client.triggerScheduledReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async executeReport(report: Report): Promise<ExecuteReportResponse> {
		try {
			const params = create(ExecuteReportRequestSchema, { report });
			const res = await this.client.executeReport(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteDashboard(id: string): Promise<DeleteDashboardResponse> {
		try {
			const params = create(DeleteDashboardRequestSchema, { id });
			const res = await this.client.deleteDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getDashboard(id: string): Promise<GetDashboardResponse> {
		try {
			const params = create(GetDashboardRequestSchema, { id });
			const res = await this.client.getDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getDashboards(ids: string[]): Promise<GetDashboardsResponse> {
		try {
			const params = create(GetDashboardsRequestSchema, { ids });
			const res = await this.client.getDashboards(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveDashboard(id: string): Promise<ArchiveDashboardResponse> {
		try {
			const params = create(ArchiveDashboardRequestSchema, {
				id,
			});
			const res = await this.client.archiveDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreDashboard(id: string): Promise<RestoreDashboardResponse> {
		try {
			const params = create(RestoreDashboardRequestSchema, { id });
			const res = await this.client.restoreDashboard(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
